<template lang="pug">
study-list.study-management(
  ref='template',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :yearSelectOptions='yearSelectOptions',
  :monthSelectOptions='monthSelectOptions',
  :searchEvent='searchEvent',
  :studyLists='studyLists',
  :candidateButtonEvent='candidateButtonEvent',
  @yearUpdated='setMonthOptions'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#tabLabel) CBT
  template(#searchButtonText) 絞り込む
  template(#titleHeader) テスト名
  template(#candidateHeader) 受験設定
  template(#subheadYearLabel) 実施年度：
  template(#subheadMonthLabel) 実施月：
  template(#titleLabel) 学年：
  template(#candidateButtonLabel) 受験可能なユーザー
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import StudyContentsApi from '@/mixins/api/StudyContentsApi'
import OriginalCurriculumsApi from '@/mixins/api/OriginalCurriculumsApi'
import StudyList from '@/components/templates/manager/StudyList.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import { StudyListType, StudyManagementItem, StudySearchParamsType } from '@/models/studyListModel'
import { InputItem } from '@/models/inputItem'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { InputItemPlaceholderSetting } from '@/const/DisplaySettings'
import { StudySearchUtils } from '@/utils/StudySearchUtils'
import { StudyManagementUtils } from '@/utils/StudyManagementUtils'

/**
 * 先生・講師・テスト管理画面
 */
@Component({
  components: {
    StudyList,
  },
})
export default class StudyManagement extends Mixins(LoginMethods, StudyContentsApi, OriginalCurriculumsApi) {
  // テンプレートへのアクセス
  @Ref() template!: StudyList

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: 'テスト管理',
    },
  ]

  // 教室長・講師権限
  private userRole: UserRoles = UserRoles.BRANCH

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_STUDY

  // 実施年度選択プルダウンのプレースホルダー
  private yearSelectPlaceholder: InputItem = StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.YEAR_REQUIED)

  // 実施月選択プルダウンのプレースホルダー
  private monthSelectPlaceholder: InputItem = StudySearchUtils.buildPlaceholder(
    InputItemPlaceholderSetting.MONTH_REQUIED
  )

  // 実施年度選択オプション
  private yearSelectOptions: InputItem[] = StudySearchUtils.buildYearInputItems(this.yearSelectPlaceholder)

  // 実施月選択オプション
  private monthSelectOptions: InputItem[] = StudySearchUtils.buildMonthInputItems(this.monthSelectPlaceholder)

  // テスト一覧表示情報
  private studyLists: StudyListType[] = []

  // 現在の検索パラメーター
  private currentSearchParams: StudySearchParamsType | null = null

  // 初期表示自動検索実行中フラグ
  private isProcessingAutomaticSearch = false

  // 教室管理画面Cookie情報
  private get branchCookie() {
    return this.$bcbtCookies.branchCookie.get() || {}
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // 実施年度選択オプションを取得
    const apiResponse = await this.getYears()
    this.yearSelectOptions = StudySearchUtils.buildYearInputItems(this.yearSelectPlaceholder, apiResponse)
    // Cookieに検索条件がセットされている場合は設定して自動検索
    const studyManagementSearch = this.branchCookie.studyManagementSearch
    if (studyManagementSearch) {
      this.isProcessingAutomaticSearch = true
      this.template.yearValue = String(studyManagementSearch.years)
      this.template.monthValue = String(studyManagementSearch.month)
      await this.search()
    }
    this.$loading.complete()
  }

  // 実施月オプションのセット処理（実施年度変更時）
  private async setMonthOptions() {
    this.$loading.start()
    // 実施年度未選択状態の場合、プルダウンは空になる
    const apiResponse = this.template.yearValue ? await this.getMonths(Number(this.template.yearValue)) : null
    this.monthSelectOptions = StudySearchUtils.buildMonthInputItems(this.monthSelectPlaceholder, apiResponse)
    // 初期表示自動検索時以外は実施月を未選択状態に戻す
    if (!this.isProcessingAutomaticSearch) {
      this.template.monthValue = ''
    }
    this.isProcessingAutomaticSearch = false
    this.$loading.complete()
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    // 1ページ目を検索
    await this.search()
  }

  // 検索処理
  private async search() {
    // 実施年月が選択されていなければ検索しない
    if (!this.template.yearValue || !this.template.monthValue) {
      return
    }
    this.$loading.start()
    // 検索パラメーターをセット
    this.currentSearchParams = {
      years: Number(this.template.yearValue),
      month: Number(this.template.monthValue),
    }
    // テスト一覧取得API呼び出し
    const apiResponse = await this.searchStudyContents(this.currentSearchParams, this.userRole)
    this.studyLists = StudyManagementUtils.formatStudyLists(apiResponse)
    // Cookieに検索条件をセット
    const branchCookie = this.branchCookie
    branchCookie.studyManagementSearch = this.currentSearchParams
    await this.$bcbtCookies.branchCookie.set(branchCookie)
    this.$loading.complete()
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 現ページのリンク押下時は遷移処理なし
    if (link === this.currentLink) return

    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Branch${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'BranchLogin' })
  }

  // 受験設定ボタン押下時イベント
  private candidateButtonEvent(item: StudyManagementItem) {
    // 受験設定画面へ遷移
    this.$router.push({
      name: 'BranchStudyManagementCandidate',
      params: { studyId: String(item.originalCurriculumId) },
    })
  }
}
</script>
